/**
 * Pluggable analytics module for Mirrably widgets.
 *
 * Consumers attach callbacks via `addCallback()`. All tracking calls flow
 * through these callbacks with a consistent event envelope. Callback errors
 * are swallowed so a misbehaving integration never breaks the widget.
 *
 * Tracked events follow a consistent schema:
 *   tryon_widget_opened, tryon_widget_closed, tryon_session_started,
 *   tryon_tracking_started, tryon_tracking_lost, tryon_variant_changed,
 *   tryon_snapshot_taken, tryon_model_loaded, tryon_error
 */

/** A single analytics event dispatched to all registered callbacks. */
export interface AnalyticsEvent {
  /** Namespaced event identifier, e.g. "tryon_widget_opened". */
  name: string;
  /** Flat property bag -- string, number, or boolean values only. */
  properties: Record<string, string | number | boolean>;
  /** Unix timestamp (ms) at the moment of the call. */
  timestamp: number;
}

/** Callback signature for analytics integrations. */
export type AnalyticsCallback = (event: AnalyticsEvent) => void;

/**
 * Analytics bus for Mirrably widgets.
 *
 * One instance is created per widget element. Consumers register callbacks to
 * receive every tracked event. The instance is passed through to the widget's
 * public API so page-level code can attach its own integrations.
 */
export class Analytics {
  private readonly callbacks: AnalyticsCallback[] = [];
  private readonly storeId: string;
  private readonly sessionId: string;

  /**
   * @param storeId - Tenant/store identifier embedded in every event.
   */
  constructor(storeId: string) {
    this.storeId = storeId;
    // Use crypto.randomUUID when available (modern browsers + jsdom 16+).
    // Fall back to a simple timestamp+random string for older environments.
    this.sessionId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  // -- Callback management ----------------------------------------------------

  /**
   * Register an analytics callback.
   *
   * The same callback reference can only be added once -- duplicate adds are
   * silently ignored to prevent double-firing.
   */
  addCallback(cb: AnalyticsCallback): void {
    if (!this.callbacks.includes(cb)) {
      this.callbacks.push(cb);
    }
  }

  /**
   * Remove a previously registered callback.
   *
   * @param cb - The exact function reference passed to `addCallback()`.
   */
  removeCallback(cb: AnalyticsCallback): void {
    const idx = this.callbacks.indexOf(cb);
    if (idx !== -1) {
      this.callbacks.splice(idx, 1);
    }
  }

  // -- Tracking ---------------------------------------------------------------

  /**
   * Fire an analytics event to all registered callbacks.
   *
   * `storeId` and `sessionId` are automatically merged into `properties` so
   * every event is scoped without repeating those values at each call site.
   *
   * @param name       - Event name (see EVENT NAMES below).
   * @param properties - Additional event-specific properties.
   */
  track(
    name: string,
    properties?: Record<string, string | number | boolean>,
  ): void {
    const event: AnalyticsEvent = {
      name,
      properties: {
        storeId: this.storeId,
        sessionId: this.sessionId,
        ...properties,
      },
      timestamp: Date.now(),
    };

    for (const cb of this.callbacks) {
      try {
        cb(event);
      } catch {
        // Swallow -- a broken integration must never crash the widget.
      }
    }
  }

  // -- Convenience: typed event helpers ----------------------------------------
  // These mirror the event names from the standard schema so callers get IDE
  // completion and we keep the event-name strings in one place.

  /** Widget overlay was opened by the user or programmatically. */
  trackWidgetOpened(productId?: string): void {
    this.track('tryon_widget_opened', {
      ...(productId !== undefined ? { productId } : {}),
    });
  }

  /** Widget overlay was closed. */
  trackWidgetClosed(durationMs: number): void {
    this.track('tryon_widget_closed', { duration_ms: durationMs });
  }

  /** Camera stream and face-tracking are both ready. */
  trackSessionStarted(): void {
    this.track('tryon_session_started');
  }

  /** First face detected after the session started or after tracking was lost. */
  trackTrackingStarted(): void {
    this.track('tryon_tracking_started');
  }

  /** Face was lost after active tracking. */
  trackTrackingLost(): void {
    this.track('tryon_tracking_lost');
  }

  /** User switched to a different product variant. */
  trackVariantChanged(variantName: string, productId?: string): void {
    this.track('tryon_variant_changed', {
      variantName,
      ...(productId !== undefined ? { productId } : {}),
    });
  }

  /** User triggered the snapshot / download flow. */
  trackSnapshotTaken(productId?: string): void {
    this.track('tryon_snapshot_taken', {
      ...(productId !== undefined ? { productId } : {}),
    });
  }

  /** 3-D model finished loading. */
  trackModelLoaded(productId: string, loadTimeMs: number): void {
    this.track('tryon_model_loaded', { productId, loadTime_ms: loadTimeMs });
  }

  /** An error occurred during a particular widget lifecycle stage. */
  trackError(error: string, stage: string): void {
    this.track('tryon_error', { error, stage });
  }

  /** User uploaded a static photo to try on products. */
  trackPhotoUploaded(productId?: string): void {
    this.track('tryon_photo_uploaded', {
      ...(productId !== undefined ? { productId } : {}),
    });
  }
}
